import { useState, useRef, useEffect } from 'react';
import { TextToSpeechService, TextToSpeechProps } from '@/services/speech/TextToSpeechService';
import { toast } from 'sonner';

// Helper function to get readable media error messages
function getMediaErrorMessage(errorCode: number): string {
  switch (errorCode) {
    case 1: return 'MEDIA_ERR_ABORTED - The user canceled the audio';
    case 2: return 'MEDIA_ERR_NETWORK - A network error occurred';
    case 3: return 'MEDIA_ERR_DECODE - Audio decoding failed';
    case 4: return 'MEDIA_ERR_SRC_NOT_SUPPORTED - Audio format not supported';
    default: return 'Unknown media error';
  }
}

export const useTextToSpeech = (props?: TextToSpeechProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const handlePlay = async () => {
    try {
      if (isGenerating) return;

      if (isPlaying && audioRef.current) {
        audioRef.current.pause();
      }

      setIsGenerating(true);
      setConfigError(null);
      
      const textToSpeak = props ? TextToSpeechService.formatTextForSpeech(props) : '';
      
      const result = await TextToSpeechService.generateSpeech(textToSpeak);
      
      if (!result?.audioUrl) {
        throw new Error('Failed to generate audio URL');
      }

      // Validate and normalize the URL
      console.log('Generated audio URL:', result.audioUrl);
      console.log('Audio URL length:', result.audioUrl.length);
      
      // Ensure URL is properly formatted for Supabase public access
      let finalUrl = result.audioUrl;
      
      // Double-check that the URL is in the correct public format
      if (finalUrl.includes('supabase.co') && !finalUrl.includes('/storage/v1/object/public/')) {
        console.warn('URL not in public format, attempting to fix:', finalUrl);
        const filename = finalUrl.split('/').pop();
        if (filename) {
          finalUrl = `https://vjrredluydfrknkcclbw.supabase.co/storage/v1/object/public/tts-audio/${filename}`;
          console.log('Rebuilt URL to public format:', finalUrl);
        }
      }
      
      console.log('Final URL for audio playback:', finalUrl);
      console.log('Final URL length:', finalUrl.length);

      // Test file accessibility and basic metadata
      try {
        console.log('Testing file accessibility...');
        const testResponse = await fetch(finalUrl, { method: 'HEAD' });
        console.log('File test response:', {
          status: testResponse.status, 
          statusText: testResponse.statusText,
          contentType: testResponse.headers.get('content-type'),
          contentLength: testResponse.headers.get('content-length'),
          ok: testResponse.ok
        });
        
        if (!testResponse.ok) {
          throw new Error(`File not accessible: ${testResponse.status} ${testResponse.statusText}`);
        }
      } catch (testError) {
        console.error('File accessibility test failed:', testError);
        throw new Error(`Audio file inaccessible: ${testError.message}`);
      }

      if (audioRef.current) {
        audioRef.current.pause();
      }

      const audio = new Audio();
      audioRef.current = audio;

      // Set up comprehensive event handlers
      audio.onended = () => setIsPlaying(false);
      audio.onpause = () => setIsPlaying(false);
      audio.onplay = () => setIsPlaying(true);
      
      audio.onerror = (e) => {
        setIsPlaying(false);
        console.error('Audio error details:', {
          event: e,
          src: audio.src,
          srcLength: audio.src.length,
          originalUrl: finalUrl,
          originalUrlLength: finalUrl.length,
          readyState: audio.readyState,
          networkState: audio.networkState,
          mediaError: audio.error
        });
        
        const errorMsg = audio.error ? 
          `Media error: ${audio.error.code} - ${getMediaErrorMessage(audio.error.code)}` :
          'Audio playback failed - media format may not be supported';
        
        console.error('Detailed audio error:', errorMsg);
        console.error('Failed URL:', audio.src);
        toast.error(errorMsg);
      };
      
      audio.onloadstart = () => console.log('Audio load started');
      audio.onloadeddata = () => console.log('Audio data loaded');
      audio.oncanplay = () => console.log('Audio can start playing');
      audio.onloadedmetadata = () => console.log('Audio metadata loaded');
      audio.onabort = () => console.log('Audio loading aborted');
      audio.onstalled = () => console.log('Audio loading stalled');
      audio.onsuspend = () => console.log('Audio loading suspended');

      // Set the final URL and load
      console.log('Setting audio.src to:', finalUrl);
      audio.src = finalUrl;
      console.log('Audio.src set to:', audio.src);
      console.log('Audio.src length:', audio.src.length);
      
      audio.crossOrigin = "anonymous"; // Try to handle CORS
      audio.load();

      try {
        await audio.play();
      } catch (playError) {
        console.error('Play error:', playError);
        throw new Error(`Playback failed: ${playError.message}`);
      }
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Could not generate speech';
      console.error('TTS Error:', errorMessage, error);
      
      if (errorMessage.includes('API key') || errorMessage.includes('GCP client initialization failed')) {
        setConfigError(errorMessage);
        toast.error(`Konfigurasjonsfeil: ${errorMessage}`);
      } else if (errorMessage.includes('NotAllowedError') || errorMessage.includes('user denied permission')) {
        setConfigError("Browseren blokkerte automatisk avspilling. Trykk play igjen.");
        toast.error("Trykk på play-knappen igjen for å starte avspilling.");
      } else {
        toast.error(`Feil ved generering/avspilling av tale: ${errorMessage}`);
      }
      setIsPlaying(false);
      if (audioRef.current) audioRef.current.pause();
    } finally {
      setIsGenerating(false);
    }
  };

  const handlePause = () => {
    if (audioRef.current && isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const speak = async (text: string, options?: { voiceName?: string; languageCode?: string }) => {
    try {
      if (isGenerating) return;

      if (isPlaying && audioRef.current) {
        audioRef.current.pause();
      }

      setIsGenerating(true);
      setConfigError(null);
      
      const result = await TextToSpeechService.generateSpeech(
        text, 
        options?.voiceName || 'nb-NO-Wavenet-E',
        options?.languageCode || 'nb-NO'
      );
      
      if (!result?.audioUrl) {
        throw new Error('Failed to generate audio URL');
      }

      if (audioRef.current) {
        audioRef.current.pause();
      }

      const audio = new Audio(result.audioUrl);
      audioRef.current = audio;

      audio.onended = () => setIsPlaying(false);
      audio.onpause = () => setIsPlaying(false);
      audio.onplay = () => setIsPlaying(true);
      audio.onerror = (e) => {
        setIsPlaying(false);
        console.error('Audio error:', e);
        toast.error('Audio playback failed');
      };

      await audio.play();
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Could not generate speech';
      console.error('TTS Error:', errorMessage, error);
      toast.error(`Feil ved generering/avspilling av tale: ${errorMessage}`);
      setIsPlaying(false);
      if (audioRef.current) audioRef.current.pause();
    } finally {
      setIsGenerating(false);
    }
  };

  return {
    isPlaying,
    isGenerating: isGenerating,
    isLoading: isGenerating,
    configError,
    handlePlay,
    handlePause,
    speak,
    stop: () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setIsPlaying(false);
    },
    pause: handlePause,
    resume: () => {
      if (audioRef.current && !isPlaying) {
        audioRef.current.play();
        setIsPlaying(true);
      }
    }
  };
};